import { useEffect, useState } from 'react'
import Client from '../../../../../client'
import { DepopSpecific, DepopSpecificValue } from '../types'
import { getErrorString } from '../../../../../utils/serverErrorUtils'

const GLOBAL_SPECIFIC_NAMES = ['brand', 'colour', 'style', 'age', 'source']

const FETCHED_SPECIFIC_NAMES = [...GLOBAL_SPECIFIC_NAMES, 'condition']

interface DepopSpecifics {
  specifics: DepopSpecific[]
  valuesByName: Record<string, DepopSpecificValue[]>
}

const fetchSpecifics = async (): Promise<DepopSpecifics> => {
  const res = await Client.depopSpecifics()
  const specifics = res.data.specifics
    .filter(s => GLOBAL_SPECIFIC_NAMES.includes(s.name))
    .sort((a, b) => GLOBAL_SPECIFIC_NAMES.indexOf(a.name) - GLOBAL_SPECIFIC_NAMES.indexOf(b.name))

  const names = res.data.specifics.map(s => s.name).filter(name => FETCHED_SPECIFIC_NAMES.includes(name))

  const valueResponses = await Promise.all(
    names.map(name => Client.depopSpecificValues(name).then(r => [name, r.data.valueRecommendations]))
  )

  return {
    specifics,
    valuesByName: valueResponses.reduce((acc, [name, values]: any) => ({ ...acc, [name]: values }), {})
  }
}

let inFlight: Promise<DepopSpecifics> | null = null

export const resetDepopSpecificsCache = () => {
  inFlight = null
}

const load = (): Promise<DepopSpecifics> => {
  if (!inFlight) {
    inFlight = fetchSpecifics().catch(e => {
      inFlight = null
      throw e
    })
  }
  return inFlight
}

export const useDepopSpecifics = () => {
  const [specifics, setSpecifics] = useState<DepopSpecific[]>([])
  const [valuesByName, setValuesByName] = useState<Record<string, DepopSpecificValue[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false

    load()
      .then(data => {
        if (cancelled) return
        setSpecifics(data.specifics)
        setValuesByName(data.valuesByName)
        setError('')
      })
      .catch(e => {
        if (!cancelled) setError(getErrorString(e) || 'Failed to load Depop specifics')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  return { specifics, valuesByName, loading, error }
}
